import React from 'react';
import { connect } from 'react-redux';

import amplitude from 'amplitude-js';
import { toggleSpinner } from 'apps/auth/actions/appAction';
import { updateExtraByName } from 'apps/auth/actions/extraAction';
import { changeFormField, changeFormFieldValue, setNewFields, updateFields } from 'apps/auth/actions/formAction';
import { setRequestDeliveryMethod } from 'apps/auth/actions/requestAction';
import { Separator } from 'apps/auth/components/common/Layout/Separator';
import EnabledPaymentMethod from 'apps/auth/components/common/paymentMethod/EnabledPaymentMethod';
import DeleteCardPopup from 'apps/auth/components/common/popups/DeleteCardPopup';
import { REQUESTS_PAYMENT_METHOD_SELECTED } from 'apps/auth/constants/amplitude/AmplitudeEvents';
import { PAYMENT_METHOD_AMPLITUDE } from 'apps/auth/constants/amplitude/AmplitudeParamName';
import { requestPaymentMethods } from 'apps/auth/constants/amplitude/AmplitudeParamValues';
import {
    ACCOUNT,
    BONUS,
    CARD,
    DC,
    DELIVERY_BANK_ACCOUNT,
    DELIVERY_BANK_CARD,
    EXTERNAL,
    PAYMENT_BANK_CARD,
    PAYMENT_BONUS,
    PAYMENT_GLOBAL_ACCOUNT,
} from 'apps/auth/constants/Constants';
import { SOURCES_FROM, SOURCES_TO_BANK_CARD } from 'apps/auth/constants/ExtraNames';
import {
    ACCOUNT_TO,
    CARD_EXP_DATE,
    DELIVERY_METHOD,
    SOURCE,
    SOURCE_CODE_FROM,
    TO_ACCOUNT_TYPE,
    TO_COMMENT,
    TO_RECIPIENT,
    TO_SORT_CODE,
} from 'apps/auth/constants/fieldsName';
import { REQUEST_FORM_NAME } from 'apps/auth/constants/formName';
import { AUTH_REQUEST_CREATE, URL_IMAGES_I } from 'apps/auth/constants/UrlConstants';
import { apiFetchData } from 'apps/auth/helpers/api';
import { logWarn } from 'apps/auth/helpers/clientLogger';
import update from 'apps/auth/helpers/Immutability';
import { useExtra, useFields } from 'apps/auth/hooks/selectors';

import AddEmptySource from './components/AddEmptySource';
import AddSource from './components/AddSource';
import DeliveryMethodLibre from './components/libre/DeliveryMethodLibre';
import PaymentMethodBonus from './components/PaymentMethodBonus';
import PaymentMethodGaAccounts from './components/PaymentMethodGaAccounts';
import SourcesList from './components/SourcesList';
import { createPayRequestFields, getFieldsForGooglePay } from './utils';
import { getField, showSourceInfo } from '../form-multi/utils';

const SelectMethod = (props) => {
    const { sourceToLibre = {}, canTransferToBankAccount } = useExtra();
    const fields = useFields(props.formName);
    const isEmptyCards = Object.keys(props.sourcesCards)
        .filter((a) => !a.includes(BONUS))
        .every((a) => a.includes(EXTERNAL));
    const isEmptyAccounts = props.sourcesAccounts ? Object.keys(props.sourcesAccounts).every((a) => a === EXTERNAL) : null;

    const amplitudePaymentMethodEvent = (method) => {
        amplitude.getInstance().logEvent(REQUESTS_PAYMENT_METHOD_SELECTED, {
            [PAYMENT_METHOD_AMPLITUDE]: method,
        });
    };
    const requestFields = (card) => {
        props.toggleSpinner(true);
        apiFetchData(AUTH_REQUEST_CREATE, { ...fields, [DELIVERY_METHOD]: { ...fields[DELIVERY_METHOD], value: DELIVERY_BANK_CARD } })
            .then((json) => {
                const newFields = json?.forms[REQUEST_FORM_NAME]?.fields;
                props.setNewFields(REQUEST_FORM_NAME, newFields).then(() => {
                    updateCardFields(card, newFields).then(() => {
                        props.addCard();
                    });
                });
            })
            .catch((error) => {
                logWarn(error);
            })
            .finally(() => props.toggleSpinner(false));
    };

    const selectCard = (card) => {
        const prevDeliveryMethod = fields?.[DELIVERY_METHOD].value;
        if (props.isCreate && prevDeliveryMethod !== DELIVERY_BANK_CARD) {
            requestFields(card);
        } else {
            updateCardFields(card).then(() => {
                props.addCard();
            });
        }
    };

    const selectAccount = (item) => {
        updateAccountFields(item).then(() => props.addAccount());
    };

    const clickHandler = (item) => {
        if (item.type === ACCOUNT) {
            amplitudePaymentMethodEvent(requestPaymentMethods.ACCOUNT);
            props.setIsNewAccount(false);
            props.setRequestDeliveryMethod(DELIVERY_BANK_ACCOUNT).then(() => selectAccount(item));
        } else if (item.type === CARD) {
            amplitudePaymentMethodEvent(requestPaymentMethods.CARD);
            props.setRequestDeliveryMethod(DELIVERY_BANK_CARD).then(() => selectCard(item));
        } else if (item.type === '1001' || item.type === '10') {
            const newFields = createPayRequestFields(fields, item, PAYMENT_BANK_CARD);
            props.updateFields(props.formName, newFields).then(() => props.selectedCard?.(item, newFields, PAYMENT_BANK_CARD));
        } else if (item.sourceType === BONUS) {
            const newFields = createPayRequestFields(fields, item, PAYMENT_BONUS);
            props.updateFields(props.formName, newFields).then(() => props.selectedBonus(newFields, PAYMENT_BONUS));
        } else if (item.type === '1006') {
            const newFields = createPayRequestFields(fields, item, PAYMENT_GLOBAL_ACCOUNT);
            props.updateFields(props.formName, newFields).then(() => props.selectedGAccount(newFields, PAYMENT_GLOBAL_ACCOUNT));
        }
    };

    const handleClickGooglePay = (e, item, code) => {
        const newFields = getFieldsForGooglePay(fields, code);
        props.updateFields(props.formName, newFields).then(() => props.selectedGooglePay(newFields));
    };

    const updateCardFields = (card, responseFields) => {
        if (props.isCreate) {
            const newFields = responseFields || fields;
            const fieldExpDate = { ...newFields[CARD_EXP_DATE] };
            const fieldSource = { ...newFields[SOURCE] };
            const fieldSourceCodeFrom = { ...newFields[SOURCE_CODE_FROM] };
            const fieldAccountTo = { ...newFields[ACCOUNT_TO] };
            if (card) {
                fieldSource.value = card.code;
                fieldSource.isError = false;

                fieldSourceCodeFrom.value = card.code;
                fieldSourceCodeFrom.isError = false;

                fieldAccountTo.value = card.pan;
                fieldAccountTo.isError = false;

                fieldExpDate.value = card.cardExpired;
                fieldExpDate.isError = false;
            } else {
                fieldSource.value = null;
                fieldSource.isError = false;

                fieldSourceCodeFrom.value = `type=${EXTERNAL}`;
                fieldSourceCodeFrom.isError = false;

                fieldAccountTo.value = null;
                fieldAccountTo.isError = false;

                fieldExpDate.value = null;
                fieldExpDate.isError = false;
            }
            return Promise.resolve(props.updateFields(props.formName, [fieldSource, fieldAccountTo, fieldExpDate, fieldSourceCodeFrom]));
        } else {
            const newFields = createPayRequestFields(fields, {}, PAYMENT_BANK_CARD);
            return Promise.resolve(props.updateFields(props.formName, newFields));
        }
    };
    const updateAccountFields = (account) => {
        if (account) {
            const fieldSourceCodeFrom = { ...fields[SOURCE_CODE_FROM] };
            const fieldSource = { ...fields[SOURCE] };
            const fieldAccountTo = { ...fields[ACCOUNT_TO] };
            fieldSource.value = account.code;
            fieldSource.isError = false;

            fieldSourceCodeFrom.value = account.code;
            fieldSourceCodeFrom.isError = false;

            fieldAccountTo.value = account.account;
            fieldAccountTo.isError = false;

            if (account.fieldsJson) {
                const reg = /'/g;
                const replaceJson = account.fieldsJson.replace(reg, '"');
                const itemJsonFields = JSON.parse(replaceJson);
                let newFields = {};
                Object.keys(itemJsonFields).forEach((key) => {
                    if (!fields[key]) {
                        newFields[key] = getField(key, itemJsonFields[key], '', true, false, '', 'text', false);
                    } else {
                        const newField = { ...fields[key] };
                        newField.value = itemJsonFields[key];
                        newField.visible = false;
                        props.changeFormField(props.formName, newField);
                    }
                });
                if (Object.keys(newFields).length > 0) {
                    props.addNewFields(props.formName, newFields);
                } else if (account.type === EXTERNAL && fields[TO_RECIPIENT]?.value) {
                    props.changeFormFieldValue(props.formName, '', TO_RECIPIENT);
                }
            }
            return Promise.resolve(props.updateFields(props.formName, [fieldAccountTo, fieldSourceCodeFrom, fieldSource]));
        } else {
            const newFields = {
                ...fields,
                [ACCOUNT_TO]: {
                    ...fields[ACCOUNT_TO],
                    value: null,
                    isError: false,
                },
                [TO_ACCOUNT_TYPE]: {
                    ...fields[TO_ACCOUNT_TYPE],
                    value: null,
                    isError: false,
                },
                [TO_SORT_CODE]: {
                    ...fields[TO_SORT_CODE],
                    value: null,
                    isError: false,
                },
                [TO_RECIPIENT]: {
                    ...fields[TO_RECIPIENT],
                    value: null,
                    isError: false,
                },
                [TO_COMMENT]: {
                    ...fields[TO_COMMENT],
                    value: null,
                    isError: false,
                },
            };
            return Promise.resolve(props.updateFields(props.formName, newFields));
        }
    };

    const addEmptySource = (sources = {}, isAccount = false, onClickHandler) => {
        return Object.keys(sources).map((key) => {
            const item = sources[key];
            if (key.indexOf(EXTERNAL) >= 0 && showSourceInfo(item)) {
                return (
                    <AddEmptySource
                        source={item}
                        key={key}
                        isAccount={isAccount}
                        onClick={onClickHandler}
                        isCreate={props.isCreate}
                        actionBanner={props.actionBanner}
                        popupRef={props.popupRef}
                        badge={props.badge}
                        amplitudePaymentMethodEvent={amplitudePaymentMethodEvent}
                        setIsNewAccount={props.setIsNewAccount}
                    />
                );
            }
        });
    };

    const addSource = (sources = {}, onClickHandler = {}, isAccount = false) => {
        return Object.keys(sources).map((key) => {
            const item = sources[key];
            if (key.indexOf(EXTERNAL) >= 0 && showSourceInfo(item)) {
                return (
                    <AddSource
                        source={item}
                        key={key}
                        onClick={() => (isAccount ? updateAccountFields() : updateCardFields()).then(() => onClickHandler(item))}
                        isAccount={isAccount}
                        amplitudePaymentMethodEvent={amplitudePaymentMethodEvent}
                        setIsNewAccount={props.setIsNewAccount}
                        iconName="card-plus"
                        variant="secondary"
                    />
                );
            }
        });
    };
    const getSourceList = (sources, sourceCode) => {
        if (sources) {
            return Object.keys(sources).map((key) => {
                const item = sources[key];
                if (key.indexOf(DC) > 0) {
                    return (
                        <SourcesList
                            key={key}
                            isSelected={key === sourceCode}
                            source={item}
                            onClick={() => clickHandler(item)}
                            actionBanner={props.actionBanner}
                            popupRef={props.popupRef}
                            badge={props.badge}
                            onExpiredCardClick={() =>
                                props.popupRef.current?.open(
                                    <DeleteCardPopup
                                        popupRef={props.popupRef}
                                        selectedCard={item}
                                        customDeleteCard={(id) => {
                                            const source = Object.values(sources).find((s) => s.id === id);
                                            const newSources = update(sources, { $unset: [source.code] });
                                            const extraName = props.isCreate ? SOURCES_TO_BANK_CARD : SOURCES_FROM;
                                            return props.updateExtraByName(extraName, newSources);
                                        }}
                                    />,
                                )
                            }
                        />
                    );
                }
            });
        } else {
            return false;
        }
    };

    const bankCard = (
        <>
            {isEmptyCards ? (
                <>{addEmptySource(props.sourcesCards, false, props.addCard)}</>
            ) : (
                <>
                    {getSourceList(props.sourcesCards, props.sourceCode)}
                    {addSource(props.sourcesCards, props.addCard, false)}
                </>
            )}
        </>
    );

    const bankAccount = (
        <>
            {canTransferToBankAccount &&
                props.isCreate &&
                (isEmptyAccounts ? (
                    <>
                        {!isEmptyCards && <Separator marginBottom="48px" />}
                        {addEmptySource(props.sourcesAccounts, true, props.addAccount)}
                    </>
                ) : (
                    <>
                        {getSourceList(props.sourcesAccounts, props.sourceCode)}
                        {addSource(props.sourcesAccounts, props.addAccount, true)}
                    </>
                ))}
        </>
    );
    const libre = (
        <>
            {props.isCreate && sourceToLibre ? (
                <DeliveryMethodLibre
                    popupRef={props.popupRef}
                    badge={props.badge}
                    actionBanner={props.actionBanner}
                    sourceToLibre={sourceToLibre}
                    chooseLibre={props.chooseLibre}
                />
            ) : null}
        </>
    );

    const componentMap = {
        BONUS: (
            <PaymentMethodBonus
                isSelected={props.sourcesBonus?.code === props.sourceCode}
                amountFrom={props.amountValue}
                commission={props.method?.commission}
                item={props.sourcesBonus}
                onClick={clickHandler}
                badge={props.badge}
                actionBanner={props.actionBanner}
                popupRef={props.popupRef}
            />
        ),
        GT_GLOBAL_ACCOUNT: (
            <PaymentMethodGaAccounts
                isSelected={props.sourcesGA?.code === props.sourceCode}
                amountFrom={props.amountValue}
                commission={props.method?.commission /** todo refactor showing errors min/max amount restriction */}
                item={props.sourcesGA}
                onClick={clickHandler}
                badge={props.badge}
                actionBanner={props.actionBanner}
                popupRef={props.popupRef}
            />
        ),
        GOOGLE_PAY: (
            <EnabledPaymentMethod
                name={props.method?.name}
                description={props.method?.description}
                commission={props.method?.commission}
                method={props.method}
                code={props.method?.code}
                src={`${URL_IMAGES_I}/google-pay-logo-56.png`}
                diskVariant="transparent"
                withoutRadius={true}
                mobilePayAlt={props.method?.name}
                badge={props.badge}
                actionBanner={props.actionBanner}
                clickHandler={handleClickGooglePay}
                popupRef={props.popupRef}
            />
        ),
        BANK_CARD: bankCard,
        BANK_ACCOUNT: bankAccount,
        LIBRE_PAYSEND_LIVE: libre,
    };
    const mapPaymentMethod = componentMap[props.method?.method];
    if (mapPaymentMethod) {
        return mapPaymentMethod;
    }
    return null;
};
export default connect(null, { toggleSpinner, setNewFields, updateFields, changeFormField, changeFormFieldValue, updateExtraByName, setRequestDeliveryMethod })(
    SelectMethod,
);
